import { useCallback } from "react";

import { Action, Stack } from "types/generated";
import { AccountContext } from "views/AccountWrapper";
import useTypedContext from "hooks/useTypedContext";
import { hasAtLeastOneRequiredAction } from "utils/access";
import { AccountViewer } from "components/form/graphql/types";
import useAacFlags from "hooks/useAacFlags";

import useHasStackAdminAccess from "./useHasStackAdminAccess";

const STACK_UPDATE_ACCESS_TYPES = [Action.StackManage];

const hasStackUpdateAccess = (viewer: AccountViewer, spaceId?: string) =>
  hasAtLeastOneRequiredAction(STACK_UPDATE_ACCESS_TYPES, viewer, spaceId);

/**
 * Hook that returns a function to check if the current user can update a stack.
 * A user can update a stack if they have stack management permissions or
 * if they have specific stack update access through custom access roles.
 * @returns {(stack?: Pick<Stack, "spaceDetails" | "space">) => boolean} Function that takes a stack space info and returns true if user can update the stack
 */
const useCanUpdateStack = (): ((stack?: Pick<Stack, "spaceDetails" | "space">) => boolean) => {
  const { customAccessRoles } = useAacFlags();
  const hasAdminAccess = useHasStackAdminAccess();
  const { viewer } = useTypedContext(AccountContext);

  return useCallback(
    (stack?: Pick<Stack, "spaceDetails" | "space">) => {
      if (!stack) {
        return false;
      }

      const hasAccess =
        hasAdminAccess(stack) ||
        (customAccessRoles && hasStackUpdateAccess(viewer, stack.spaceDetails?.id || stack.space));

      if (hasAccess) {
        return true;
      }

      return false;
    },
    [hasAdminAccess, viewer, customAccessRoles]
  );
};

export default useCanUpdateStack;
